/**
 * Heroes tab (spec sections 8, 59, 74).
 *
 * Four rules from the spec are visible in this file, and each one prevents a
 * specific lie:
 *
 * - `starStage` is printed as a number and labelled "star stage". It is NOT
 *   drawn as a row of stars, because the encoding is unverified and a row of
 *   five stars would assert a scale nobody has confirmed.
 * - Equipment is keyed by its slot id (`sid`), never by array position.
 * - A hero or equipment id that is not in the catalog renders as the raw id
 *   with an "unverified" marker, not as a guessed name.
 * - Opaque Jeabs stats keep their raw keys. A candidate label is shown as a
 *   candidate, with its confidence, and never as the stat's name.
 *
 * Hero names, skill names and stat candidate labels come from the server's
 * catalog and are rendered verbatim in both locales. Translating a catalog
 * label here would be this app inventing a name for something it was handed.
 */

import type { PlayerHeroesResponse } from '@kso/schemas';
import { usePlayerHeroes } from '../../../api/queries';
import { Badge, Card, Notice, Value } from '../../../components/ui/primitives';
import { EmptyState, QueryBoundary } from '../../../components/ui/states';
import { FreshnessBadge } from '../../../components/freshness/Freshness';
import { WarningList } from '../../../components/ui/primitives';
import { formatNumber, formatRatioAsPercent } from '../../../lib/format';
import { useT } from '../../../i18n';

type Hero = PlayerHeroesResponse['heroes'][number];

export function HeroesTab({ playerId }: { playerId: string }) {
  const heroes = usePlayerHeroes(playerId);
  const t = useT();

  return (
    <QueryBoundary
      query={heroes}
      loadingLabel={t.heroes.loading}
      isEmpty={(data) => data.heroes.length === 0 && data.opaqueStats.length === 0}
      empty={<EmptyState title={t.heroes.emptyTitle} body={t.heroes.emptyBody} />}
    >
      {(data) => (
        <div className="stack">
          <div className="row">
            <FreshnessBadge label={t.freshness.sources.heroGear} info={data.freshness} />
            <Badge tone={data.rosterCompleteness === 'KNOWN_PARTIAL' ? 'MEDIUM' : 'UNKNOWN'}>
              {t.heroes.rosterBadge(t.heroes.rosterCompleteness[data.rosterCompleteness])}
            </Badge>
          </div>

          <Notice severity="INFO">{t.heroes.rosterNote[data.rosterCompleteness]}</Notice>

          <WarningList warnings={data.warnings} />

          {data.heroes.length === 0 ? (
            <EmptyState title={t.heroes.noHeroesTitle} body={t.heroes.noHeroesBody} />
          ) : (
            <div className="grid grid--halves">
              {data.heroes.map((hero) => (
                <HeroCard key={`${hero.heroId}-${hero.slot ?? 'x'}`} hero={hero} />
              ))}
            </div>
          )}

          <OpaqueStats stats={data.opaqueStats} />
        </div>
      )}
    </QueryBoundary>
  );
}

function HeroCard({ hero }: { hero: Hero }) {
  const t = useT();

  return (
    <Card
      title={
        <span className="row">
          {hero.name ? (
            hero.name
          ) : (
            <span className="mono" title={t.heroes.rawHeroIdTitle}>
              {t.heroes.rawHeroId(hero.heroId)}
            </span>
          )}
          {hero.catalogVerified ? null : (
            <Badge tone="UNKNOWN" title={t.heroes.unverifiedIdTitle}>
              {t.heroes.unverifiedId}
            </Badge>
          )}
        </span>
      }
    >
      <dl className="kv">
        <dt>{t.heroes.level}</dt>
        <dd>
          <Value absent={t.heroes.notReported}>{formatNumber(hero.level)}</Value>
        </dd>

        <dt title={t.heroes.starStageTitle}>{t.heroes.starStage}</dt>
        <dd>
          <Value absent={t.heroes.notReported}>{hero.starStage}</Value>
        </dd>

        <dt>{t.heroes.slotPosition}</dt>
        <dd>
          <Value absent={t.common.dash}>{hero.slot}</Value> /{' '}
          <Value absent={t.common.dash}>{hero.position}</Value>
        </dd>

        <dt>{t.heroes.exclusiveEquipment}</dt>
        <dd>
          {hero.exclusiveEquipId ? (
            <span className="mono">
              {hero.exclusiveEquipId}
              {hero.exclusiveEquipLevel !== null
                ? t.heroes.equipLevel(hero.exclusiveEquipLevel)
                : ''}
            </span>
          ) : (
            <span className="absent">{t.heroes.noneRecorded}</span>
          )}
        </dd>
      </dl>

      <div style={{ marginTop: 10 }}>
        <h3 className="muted small">{t.heroes.skills}</h3>
        {hero.skills.length === 0 ? (
          <p className="absent small" style={{ margin: 0 }}>
            {t.heroes.noSkills}
          </p>
        ) : (
          <ul style={{ margin: '4px 0 0', paddingLeft: 0, listStyle: 'none' }}>
            {hero.skills.map((skill) => (
              <li key={skill.skillId} className="row small" style={{ gap: 6 }}>
                <span className={skill.name ? undefined : 'mono'}>
                  {skill.name ?? t.heroes.rawSkillId(skill.skillId)}
                </span>
                <span className="muted">
                  {skill.level === null ? (
                    <span className="absent">{t.heroes.skillLevelUnknown}</span>
                  ) : (
                    t.heroes.skillLevel(skill.level)
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
        <p className="faint small" style={{ marginBottom: 0 }}>
          {t.heroes.skillOrderNote}
        </p>
      </div>

      <div style={{ marginTop: 10 }}>
        <h3 className="muted small">{t.heroes.equipmentBySlot}</h3>
        {hero.equipment.length === 0 ? (
          <p className="absent small" style={{ margin: 0 }}>
            {t.heroes.noEquipment}
          </p>
        ) : (
          <div className="table-wrap" style={{ marginTop: 4 }}>
            <table className="data">
              <thead>
                <tr>
                  <th title={t.heroes.equipmentColumns.slotIdTitle}>
                    {t.heroes.equipmentColumns.slotId}
                  </th>
                  <th>{t.heroes.equipmentColumns.equipment}</th>
                  <th className="num">{t.heroes.equipmentColumns.enhance}</th>
                  <th className="num">{t.heroes.equipmentColumns.refine}</th>
                </tr>
              </thead>
              <tbody>
                {[...hero.equipment]
                  .sort((a, b) => a.slotId - b.slotId)
                  .map((item) => (
                    <tr key={`${item.slotId}-${item.equipmentId}`}>
                      <td className="mono">{item.slotId}</td>
                      <td className="mono">
                        {item.equipmentId}
                        {item.catalogVerified ? null : (
                          <>
                            {' '}
                            <Badge tone="UNKNOWN" title={t.heroes.unverifiedEquipmentTitle}>
                              {t.heroes.unverified}
                            </Badge>
                          </>
                        )}
                      </td>
                      <td className="num">
                        <Value absent={t.common.dash}>{item.enhancementLevel}</Value>
                      </td>
                      <td className="num">
                        <Value absent={t.common.dash}>{item.refinementLevel}</Value>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}

function OpaqueStats({ stats }: { stats: PlayerHeroesResponse['opaqueStats'] }) {
  const t = useT();

  return (
    <Card title={t.heroes.opaqueTitle}>
      <p className="muted small" style={{ marginTop: 0 }}>
        {t.heroes.opaqueBlurb}
      </p>

      {stats.length === 0 ? (
        <p className="absent small" style={{ margin: 0 }}>
          {t.heroes.opaqueEmpty}
        </p>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>{t.heroes.opaqueColumns.key}</th>
                <th className="num">{t.heroes.opaqueColumns.value}</th>
                <th>{t.heroes.opaqueColumns.candidateMeaning}</th>
                <th className="num">{t.heroes.opaqueColumns.candidateConfidence}</th>
              </tr>
            </thead>
            <tbody>
              {stats.map((stat) => (
                <tr key={stat.key}>
                  <td className="mono">stats[&quot;{stat.key}&quot;]</td>
                  <td className="num">{formatNumber(stat.value)}</td>
                  <td>
                    {stat.candidateName ? (
                      <span className="row" style={{ gap: 6 }}>
                        <span className={stat.verified ? undefined : 'muted'}>
                          {stat.candidateName}
                        </span>
                        <Badge tone={stat.verified ? 'CONFIRMED' : 'UNKNOWN'}>
                          {stat.verified ? t.heroes.verified : t.heroes.candidateOnly}
                        </Badge>
                      </span>
                    ) : (
                      <span className="absent">{t.heroes.noCandidate}</span>
                    )}
                  </td>
                  <td className="num">
                    {stat.confidence === null ? (
                      <span className="absent">{t.common.dash}</span>
                    ) : (
                      formatRatioAsPercent(stat.confidence)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
